import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Megaphone, Save, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const noticeImgUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const MODES = [
  { id: "text", label: "Texto" },
  { id: "oven", label: "Pizzas no forno" },
  { id: "image", label: "Imagem / cartaz" },
];
const fieldCls = "bg-[#0C0A09] border border-[#292524] text-stone-100 rounded-md px-3 py-2 text-sm";

function OvenItems({ items, onChange }) {
  const update = (idx, k, v) => onChange(items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2" data-testid="notice-oven-editor">
      {items.map((it, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input type="number" min="1" value={it.qty} onChange={(e) => update(idx, "qty", e.target.value)}
            data-testid={`notice-oven-qty-${idx}`} className={`${fieldCls} w-20`} />
          <input value={it.name} onChange={(e) => update(idx, "name", e.target.value)} placeholder="Ex.: Pizza Portuguesa"
            data-testid={`notice-oven-name-${idx}`} className={`${fieldCls} flex-1`} />
          <button onClick={() => onChange(items.filter((_, i) => i !== idx))} data-testid={`notice-oven-remove-${idx}`}
            className="p-2 rounded-full text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Remover">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={() => onChange([...items, { name: "", qty: 1 }])} data-testid="notice-oven-add-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-4 py-2 text-xs hover:bg-[#292524] transition-colors">
        <Plus className="w-3.5 h-3.5" /> Adicionar pizza
      </button>
    </div>
  );
}

function ImagePicker({ image, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagem enviada! Clique em Salvar para guardar.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar a imagem.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div data-testid="notice-image-editor">
      {image && <img src={noticeImgUrl(image)} alt="Cartaz" data-testid="notice-image-preview" className="max-h-56 w-auto rounded-lg mb-2 border border-[#292524]" />}
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="notice-image-file" />
      <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="notice-image-upload-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-4 py-2 text-xs hover:bg-[#292524] transition-colors disabled:opacity-60">
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {uploading ? "Enviando..." : image ? "Trocar imagem" : "Enviar imagem"}
      </button>
    </div>
  );
}

export default function NoticeSettings() {
  const [notice, setNotice] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setNotice(data.notice);
      setSaved(data.notice);
    }).catch(() => {});
  }, []);

  if (!notice) return null;
  const patch = (p) => setNotice((n) => ({ ...n, ...p }));

  const save = async (next) => {
    setSaving(true);
    try {
      const body = { ...next, items: next.items.map((i) => ({ name: i.name, qty: parseInt(i.qty, 10) || 1 })) };
      const { data } = await api.put("/admin/notice", body);
      setNotice(data.notice);
      setSaved(data.notice);
      toast.success(data.notice.enabled ? "Aviso ativado!" : "Aviso salvo (desativado).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar o aviso.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mb-6" data-testid="notice-settings">
      <div className="bg-[#1C1917] border border-[#292524] rounded-xl p-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Megaphone className="w-4 h-4" /> Aviso para clientes
          </p>
          <span data-testid="notice-status-badge" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${saved.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-stone-500/15 text-stone-400"}`}>
            {saved.enabled ? `Ativado · ${MODES.find((m) => m.id === saved.mode)?.label}` : "Desativado"}
          </span>
        </div>
        <p className="text-xs text-stone-500 mb-3">
          Escolha o tipo de aviso. Só um fica ativo por vez. O cliente vê o aviso 3 segundos depois de abrir o site.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => patch({ mode: m.id })} data-testid={`notice-mode-${m.id}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${notice.mode === m.id ? "bg-primary text-primary-foreground border-primary" : "border-[#292524] text-stone-400 hover:text-white"}`}>
              {m.label}
            </button>
          ))}
        </div>
        {notice.mode === "text" && (
          <textarea data-testid="notice-text-input" value={notice.text} onChange={(e) => patch({ text: e.target.value })}
            rows={4} placeholder="Ex.: Hoje teremos promoção de pizza grande!" className={`${fieldCls} w-full`} />
        )}
        {notice.mode === "oven" && <OvenItems items={notice.items} onChange={(items) => patch({ items })} />}
        {notice.mode === "image" && <ImagePicker image={notice.image} onChange={(image) => patch({ image })} />}
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => save({ ...notice, enabled: !notice.enabled })} disabled={saving} data-testid="notice-toggle-btn"
            className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${notice.enabled ? "border border-[#292524] text-stone-200 hover:bg-[#292524]" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
            {notice.enabled ? "Desativar aviso" : "Ativar aviso"}
          </button>
          <button onClick={() => save(notice)} disabled={saving} data-testid="notice-save-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-5 py-2.5 text-sm hover:bg-[#292524] transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
